import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import classnames from "classnames";
import { UpdateDispatchContext } from "components/SummonerProfiles/SummonerProfileUpdateReducer";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import RankFilters from "./RankFilters";
import { useProfileRanks } from "api/summonerProfileApi";
import { getS13RankUrl } from "@outplayed/riot-assets";
import { selectRank } from "@ugg/shared/utils/rank-helpers";
import ArrowButton from "components/SummonerProfiles/views/ProfileOverview/MatchHistory/ArrowButton.js";
import { getQueueType, capitalizeFirstLetter, convertRomanNumeral, numberWithCommas } from "lib/general-helper";
import { defaultOverviewFilters } from "components/SummonerProfiles/views/ProfileOverview/overview-filters";
import LPBlockContainer from "components/SummonerProfiles/views/ProfileOverview/LPBlock/LPBlockContainer";
import { MediaQuery } from "@outplayed/responsive";
import { useGlobal } from "reactn";

const displayRankText = (tier, rank) => {
  if (!tier) {
    return "Unranked";
  }

  switch (tier.toLowerCase()) {
    case "master":
      return "Master";
    case "grandmaster":
      return "Grandmaster";
    case "challenger":
      return "Challenger";
    default:
      return `${capitalizeFirstLetter(tier)} ${convertRomanNumeral(rank)}`;
  }
};

const Promos = ({ promos }) => {
  return (
    <div className="promos">
      {(promos || "").split("").map((outcome, index) => {
        const promoGameClassNames = classnames("promo_game", {
          promo_game__win: outcome === "W",
          promo_game__loss: outcome === "L",
          promo_game__unfinished: !outcome,
        });
        return <div key={index} className={promoGameClassNames} />;
      })}
    </div>
  );
};

const SummonerRank = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [windowBreakpoint] = useGlobal("responsive");
  const collapseWindow = ["MOBILE_LARGE", "TABLET", "DESKTOP_SMALL"].includes(windowBreakpoint);

  const { lastUpdatedAt, losses, lp, promoProgress, queueType, rank, role, seasonId, tier, wins } = props.rankData || {};

  useEffect(() => {
    if (collapseWindow && isOpen) {
      setIsOpen(false);
    }
  }, [windowBreakpoint]);

  const { queue, refetchedLP } = props;
  const queueTypeLabel = getQueueType(queueType);
  const winRate = Math.round(((wins || 0) / ((wins || 0) + (losses || 0))) * 100);
  const totalGames = (wins || 0) + (losses || 0);

  return (
    <div
      className={classnames("rank-lp-container", "content-section", "content-section_no-padding", {
        "unranked-container": !tier,
      })}
    >
      <div className={classnames("rank-content", { is_open: isOpen })}>
        <div className="queue-container">
          <div className="rank-content-header">
            <div className="title">
              <div className="bluebar" />
              <div className="queue-type">{props.queueTypeLabel || getQueueType(queueType)}</div>
            </div>
            {tier ? <ArrowButton isOpen={isOpen} toggleFunction={setIsOpen} /> : null}
          </div>
          <span className="unranked">{tier ? null : displayRankText(tier, rank)}</span>
        </div>
        {tier ? (
          <div className="rank-sub-content">
            <div className="rank-image-container">
              {tier ? <img className="rank-img" src={getS13RankUrl(tier.toLowerCase())} /> : null}
            </div>
            <div className="text-container">
              <div className="rank-text">
                <span className="rank-title">{displayRankText(tier, rank)}</span>
                {tier && (
                  <>{promoProgress ? <Promos promos={promoProgress} /> : <span>{`${numberWithCommas(lp || 0)} LP`}</span>}</>
                )}
              </div>
              {totalGames > 0 && (
                <div className="rank-wins">
                  <span className="total-games">
                    {wins}W {losses}L
                  </span>
                  <span>{`${winRate}% Win Rate`}</span>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
      {tier && isOpen && (
        <div className="rank-block-lp-graph">
          <div className="graph-container">
            <LPBlockContainer refetchedLP={refetchedLP} queue={queue} tier={tier} />
          </div>
        </div>
      )}
    </div>
  );
};

const defaultFilters = {
  seasonId: defaultOverviewFilters.rankBlock.seasonId,
};
const RankBlock = (props) => {
  const { refetchedLP } = props;
  const dispatch = useContext(UpdateDispatchContext);
  const context = getSummonerProfileContext();
  const { summonerName, region } = context;
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);

  const { data, loading, error, refetch, variables } = useProfileRanks(
    summonerName,
    region,
    { seasonId: filters.seasonId },
    { fetchPolicy: !filtersChanged ? "cache-first" : "network-only" },
  );
  const { fetchProfileRanks } = data || {};
  const { rankScores } = fetchProfileRanks || {};

  useEffect(() => {
    setFilters(defaultFilters);
  }, [summonerName, region]);

  useEffect(() => {
    dispatch({
      type: "addUpdates",
      payload: { rankBlock: refetch },
    });
  }, [JSON.stringify(variables)]);

  let content = null;
  if (loading) {
    content = (
      <div className="rank-block_loading">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="rank-list">
        <SummonerRank
          rankData={selectRank("ranked_solo_5x5", filters.seasonId, rankScores)}
          queueTypeLabel="Ranked Solo"
          refetchedLP={refetchedLP}
          queue="ranked_solo"
        />
        <SummonerRank
          rankData={selectRank("ranked_flex_sr", filters.seasonId, rankScores)}
          queueTypeLabel="Ranked Flex"
          refetchedLP={refetchedLP}
          queue="ranked_flex"
        />
      </div>
    );
  }

  return (
    <div className="rank-block">
      {/* <div className="content-section_header rank-block_filters">
        <span>Rank</span>
        <RankFilters
          filters={filters}
          setFilters={setFilters}
          setFiltersChanged={setFiltersChanged}
        />
      </div> */}
      {content}
    </div>
  );
};

export default React.memo(RankBlock);
